import { useState, useCallback } from "react";
import { Menu, MenuItem } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ToolbarButton, useGridApiContext } from "@mui/x-data-grid";
import { buildFullCsv } from "../../../utils/CsvExportUtils.js";
import { saveAs } from "../../../utils/DownloadHelper.js";
import { useAsyncDataGridExport } from "./AsyncDataGridExportContext.js";

const MENU_ITEMS = {
  defaultCsv: "Export CSV – Výchozí",
  fullCsv: "Export CSV – Plný",
  fullJson: "Export JSON – Plný",
};

/**
 * Export dropdown for AsyncDataGrid (replaces default CSV export button).
 */
function AsyncDataGridExportMenu() {
  const apiRef = useGridApiContext();
  const { rows, loading, exportConfig, gridColumns, isServerMode } = useAsyncDataGridExport();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const exportDisabled = loading || rows.length === 0;
  const plnyDisabled = exportDisabled || isServerMode;
  const { filename } = exportConfig;

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleDefaultCsv = useCallback(() => {
    apiRef.current.exportDataAsCsv({ fileName: `${filename}-vychozi` });
    handleClose();
  }, [apiRef, filename, handleClose]);

  const handleFullCsv = useCallback(() => {
    saveAs({
      content: buildFullCsv(rows, { exportConfig, gridColumns }),
      fileName: `${filename}-plny.csv`,
      mimeType: "text/csv;charset=utf-8",
    });
    handleClose();
  }, [rows, exportConfig, gridColumns, filename, handleClose]);

  const handleFullJson = useCallback(() => {
    saveAs({
      content: JSON.stringify(rows, null, 2),
      fileName: `${filename}-plny.json`,
      mimeType: "application/json",
    });
    handleClose();
  }, [rows, filename, handleClose]);

  return (
    <>
      <ToolbarButton
        aria-label="Export"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={exportDisabled}>
        <FileDownloadOutlinedIcon fontSize="small" />
      </ToolbarButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleDefaultCsv} disabled={exportDisabled}>
          {MENU_ITEMS.defaultCsv}
        </MenuItem>
        <MenuItem onClick={handleFullCsv} disabled={plnyDisabled}>
          {MENU_ITEMS.fullCsv}
        </MenuItem>
        <MenuItem onClick={handleFullJson} disabled={plnyDisabled}>
          {MENU_ITEMS.fullJson}
        </MenuItem>
      </Menu>
    </>
  );
}

export default AsyncDataGridExportMenu;
